import AudienceTree from '@/components/audience/AudienceTree';
import AudienceMembersPanel from '@/components/audience/AudienceMembersPanel';
import { FormEvent, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useBranding } from '@/context/BrandingContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useAudienceCatalog } from '@/hooks/useAudienceCatalog';
import { audienceService, AudienceNode, AudienceNodeType, nodePath, isPartner } from '@/services/audienceService';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import Select from '@/components/ui/Select';
import LocationTargetSelector from '@/components/ads/LocationTargetSelector';

type NodeDraft = Omit<AudienceNode, 'id'> & { id?: string };

export default function Audiences() {
  const { branding, isLoading: brandingLoading } = useBranding();
  const { t } = useTranslation();
  const copy = t.audience;
  const enabled = Boolean(branding.modules?.audienceManagementEnabled);
  const query = useAudienceCatalog(enabled);
  const client = useQueryClient();
  const { confirm, dialog } = useConfirmDialog();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [node, setNode] = useState<NodeDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const nodes = query.data?.nodes ?? [];
  const layoutOptions = [
    { value: 'DEFAULT', label: t.applicationSettings.layouts.default },
    { value: 'VERTICAL_MENU', label: t.applicationSettings.layouts.verticalMenu },
    { value: 'HOSPITALITY', label: t.applicationSettings.layouts.hospitality },
  ];

  const editNode = (value: NodeDraft) => {
    setSaveError(null);
    setNode(nodes.find(n => n.id === value.id) ?? value);
  };
  const close = () => {
    if (saving) return;
    setNode(null);
    setSaveError(null);
  };

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaveError(null);
    setSaving(true);
    try {
      if (node) await audienceService.saveNode(node);
      await client.invalidateQueries({ queryKey: ['audience-catalog'] });
      setNode(null);
      toast.success(copy.saved);
    } catch (error) {
      const message = error instanceof Error ? error.message : t.errors.unexpectedError;
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(item: AudienceNode) {
    await confirm({
      title: copy.deleteNode,
      message: copy.deleteConfirmation.replace('{name}', item.name),
      confirmLabel: copy.delete,
      cancelLabel: copy.cancel,
      confirmVariant: 'danger',
      onConfirm: async () => {
        try {
          await audienceService.deleteNode(item.id);
          await client.invalidateQueries({ queryKey: ['audience-catalog'] });
          toast.success(copy.deleted);
        } catch (error) {
          toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
          throw error;
        }
      },
    }).catch(() => false);
  }

  const modalTitle = node?.id ? copy.editNode : copy.newNode;

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="w-full space-y-4 px-4 sm:px-6 lg:px-8">
        {brandingLoading || (enabled && query.isPending) ? (
          <div role="status" className="flex items-center justify-center gap-2 rounded-lg bg-white py-12 text-gray-500"><Loader2 className="h-5 w-5 animate-spin" />{copy.loading}</div>
        ) : !enabled ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">{copy.disabled}</div>
        ) : query.isError ? (
          <div role="alert" className="space-y-3 rounded-lg border border-red-200 bg-white p-6"><p className="text-sm text-red-600">{copy.loadError}</p><Button variant="outline" onClick={() => query.refetch()}>{copy.retry}</Button></div>
        ) : (
              <div className={selectedId && nodes.some(n => n.id === selectedId) ? 'grid items-start gap-4 xl:grid-cols-2' : ''}>
                <div className="min-w-0"><AudienceTree nodes={nodes} selected={selectedId ?? undefined}
                  onSelect={item => setSelectedId(item.id)} onEdit={editNode} onDelete={item => { void remove(item); }}
                  onCreate={() => editNode({ name: '', type: 'COMPLEX' })}
                  onCreateChild={async child => {
                    await audienceService.saveNode(child);
                    await client.invalidateQueries({ queryKey: ['audience-catalog'] });
                    toast.success(copy.saved);
                  }} /></div>
                {nodes.find(n => n.id === selectedId) && <AudienceMembersPanel key={selectedId} node={nodes.find(n => n.id === selectedId)!} nodes={nodes} onClose={() => setSelectedId(null)} />}
              </div>

        )}
      </div>
      {dialog}
      <Modal isOpen={Boolean(node)} onClose={close} title={modalTitle} size="xl" closeOnBackdrop={!saving} closeOnEsc={!saving}>
        <form onSubmit={save} className="space-y-5">
          {saveError && <div role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">{saveError}</div>}
          <fieldset disabled={saving} className="space-y-5">
            <Input label={copy.name} aria-label={copy.name} required maxLength={150} value={node?.name ?? ''}
              onChange={e => node && setNode({ ...node, name: e.target.value })} />
            {node && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select className="cursor-pointer disabled:cursor-not-allowed" label={copy.type} aria-label={copy.type} disabled={Boolean(node.id)} value={node.type} options={Object.entries(copy.types).map(([value, label]) => ({ value, label }))}
                    onChange={e => setNode({ ...node, type: e.target.value as AudienceNodeType, parentId: ['PARTNER', 'PROGRAM'].includes(e.target.value) ? null : node.parentId })} />
                  {!isPartner(node as AudienceNode) && <Select className="cursor-pointer disabled:cursor-not-allowed" label={copy.parent} aria-label={copy.parent} disabled={Boolean(node.id)} value={node.parentId ?? ''} allowEmpty emptyOptionLabel={copy.none}
                    options={nodes.filter(n => !isPartner(n) && !['ROOM', 'HOUSE', 'APARTMENT'].includes(n.type)).map(n => ({ value: n.id, label: nodePath(n.id, nodes) }))}
                    onChange={e => setNode({ ...node, parentId: e.target.value || null })} />}
                  <Select className="cursor-pointer disabled:cursor-not-allowed" label={copy.layout} aria-label={copy.layout} value={node.layout ?? ''} allowEmpty emptyOptionLabel={copy.inherit} options={layoutOptions}
                    onChange={e => setNode({ ...node, layout: (e.target.value || null) as AudienceNode['layout'] })} />
                </div>
                <p className="text-xs text-gray-500">{copy.layoutInheritance}</p>
                <p className="text-xs text-gray-500">{copy.structureHelp}</p>
                <div className="border-t border-gray-200 pt-4">
                  <LocationTargetSelector title={copy.geography} help={copy.locationInheritance}
                    value={node.countryId ? [{ countryId: node.countryId, adminAreaId: node.adminAreaId ?? undefined, localityId: node.localityId ?? undefined }] : []}
                    onChange={values => { const value = values[values.length - 1]; setNode({ ...node, countryId: value?.countryId ?? null, adminAreaId: value?.adminAreaId ?? null, localityId: value?.localityId ?? null }); }} />
                </div>
              </>
            )}
          </fieldset>
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <Button type="button" variant="outline" disabled={saving} onClick={close}>{copy.cancel}</Button>
            <Button type="submit" loading={saving}>{copy.save}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
